import type {
  ImageGenerationRequest,
  GeneratedImage,
  StyleReference,
  StyleReferenceUpload,
  CoverDesign,
  ImageBlock,
  PublishConfig,
} from '../types/images';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || error.message || 'Request failed');
  }
  return response.json();
}

export const imageApi = {
  // Image Generation
  generate: async (request: ImageGenerationRequest): Promise<{ success: boolean; image: GeneratedImage }> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 90000); // 90 second timeout

    try {
      const response = await fetch(`${API_BASE}/images/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(request),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return handleResponse(response);
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Image generation timed out. Please try again.');
      }
      throw error;
    }
  },

  getGeneratedImages: async (): Promise<{ images: GeneratedImage[] }> => {
    const response = await fetch(`${API_BASE}/images`, {
      credentials: 'include',
    });
    return handleResponse(response);
  },

  deleteGeneratedImage: async (id: string): Promise<{ success: boolean }> => {
    const response = await fetch(`${API_BASE}/images/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    return handleResponse(response);
  },

  // Style Library (RAG)
  uploadStyleReference: async (upload: StyleReferenceUpload): Promise<{ success: boolean; reference: StyleReference }> => {
    const formData = new FormData();
    formData.append('image', upload.file);
    formData.append('name', upload.name);
    formData.append('category', upload.category);
    formData.append('tags', JSON.stringify(upload.tags));
    if (upload.description) {
      formData.append('description', upload.description);
    }

    const response = await fetch(`${API_BASE}/style-library/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    return handleResponse(response);
  },

  getStyleReferences: async (category?: string): Promise<{ references: StyleReference[] }> => {
    const url = new URL(`${API_BASE}/style-library`, window.location.origin);
    if (category) url.searchParams.set('category', category);

    const response = await fetch(url.toString(), {
      credentials: 'include',
    });
    return handleResponse(response);
  },

  deleteStyleReference: async (id: string): Promise<{ success: boolean }> => {
    const response = await fetch(`${API_BASE}/style-library/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    return handleResponse(response);
  },

  findSimilarReferences: async (embedding: number[], limit?: number): Promise<{ references: StyleReference[] }> => {
    const response = await fetch(`${API_BASE}/style-library/similar`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ embedding, limit }),
    });
    return handleResponse(response);
  },

  // Cover Designer
  saveCoverDesign: async (cover: Omit<CoverDesign, 'id'>): Promise<{ success: boolean; cover: CoverDesign }> => {
    const response = await fetch(`${API_BASE}/images/cover`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(cover),
    });
    return handleResponse(response);
  },

  getCoverDesigns: async (sessionId?: string): Promise<{ covers: CoverDesign[] }> => {
    const url = new URL(`${API_BASE}/images/covers`, window.location.origin);
    if (sessionId) url.searchParams.set('sessionId', sessionId);

    const response = await fetch(url.toString(), {
      credentials: 'include',
    });
    return handleResponse(response);
  },

  deleteCoverDesign: async (id: string): Promise<{ success: boolean }> => {
    const response = await fetch(`${API_BASE}/images/cover/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    return handleResponse(response);
  },

  // Image Blocks
  saveImageBlock: async (sessionId: string, block: Omit<ImageBlock, 'id'>): Promise<{ success: boolean; block: ImageBlock }> => {
    const response = await fetch(`${API_BASE}/images/blocks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ sessionId, ...block }),
    });
    return handleResponse(response);
  },

  getImageBlocks: async (sessionId: string): Promise<{ blocks: ImageBlock[] }> => {
    const response = await fetch(`${API_BASE}/images/blocks/${sessionId}`, {
      credentials: 'include',
    });
    return handleResponse(response);
  },

  updateImageBlock: async (id: string, updates: Partial<ImageBlock>): Promise<{ success: boolean; block: ImageBlock }> => {
    const response = await fetch(`${API_BASE}/images/blocks/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(updates),
    });
    return handleResponse(response);
  },

  deleteImageBlock: async (id: string): Promise<{ success: boolean }> => {
    const response = await fetch(`${API_BASE}/images/blocks/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    return handleResponse(response);
  },

  // Publishing
  exportDocument: async (
    config: PublishConfig,
    sections: { title: string; content: string; images?: { url: string; caption?: string; position: string }[] }[],
    cover?: { imageUrl: string; title: string; author: string }
  ): Promise<Blob> => {
    const response = await fetch(`${API_BASE}/publish/export`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        ...config,
        sections,
        cover,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Export failed' }));
      throw new Error(error.error || 'Export failed');
    }

    return response.blob();
  },

  // Upload image from URL or base64
  uploadImage: async (
    imageData: string,
    filename: string
  ): Promise<{ success: boolean; url: string }> => {
    const response = await fetch(`${API_BASE}/images/upload`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ imageData, filename }),
    });
    return handleResponse(response);
  },
};
